import http from '../../shared/http.js';
import { initLiveList } from '../../shared/liveList.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// The queue of customers the job-ad import created and nobody has placed yet
// (N06).
//
// One decision per row, and which field it writes depends on who is reading:
// a manager picks the branch, a Filialleiter picks the responsible user. The
// server decides which select the row carries, so this module only reads
// whichever one is there - it never needs to know the reader's role.
//
// Both go through the ordinary customer PATCH. The field is in its whitelist
// and canEditKunde already allows exactly these two cases, so no endpoint of
// its own is needed.

function fehlermeldung(err) {
  return err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
}

export function initKundeZuordnung() {
  const live = initLiveList({
    results: 'kunde-zuordnung-list',
    path: '/kunden/zuordnung',
    forms: ['kunde-zuordnung-search']
  });
  if (!live) return;

  const list = document.getElementById('kunde-zuordnung-list');

  // One delegated listener for the region, so the rows that arrive with each
  // refreshed fragment are live without rebinding anything.
  list.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-action="zuordnen"]');
    if (!button) return;

    const zeile = button.closest('[data-kunde-id]');
    if (!zeile) return;

    const select = zeile.querySelector('select[name]');
    if (!select) return;

    // Nothing chosen. Said on the spot rather than sent and refused, because
    // the server would answer with a validation error about a field the
    // reader never saw named.
    if (!select.value) {
      notifyError('Bitte zuerst eine Auswahl treffen.');
      select.focus();
      return;
    }

    button.disabled = true;
    showLoading('Kunde wird zugeordnet …');

    try {
      await http.patch(`/kunden/${zeile.dataset.kundeId}`, { [select.name]: select.value });
      // Refetch rather than reload: the row leaves the queue while the search
      // and the scroll position stay put.
      await live.refresh();
      hideLoading();
      notifySuccess('Kunde wurde zugeordnet.');
    } catch (err) {
      hideLoading();
      button.disabled = false;
      notifyError(fehlermeldung(err));
    }
  });

  // The batch bar, beside the one-per-row queue rather than instead of it.
  // Hidden until something is ticked, so the page is the queue it has always
  // been until the reader asks for more.
  const leiste = document.getElementById('kunde-sammel-leiste');
  const anzahlAnzeige = document.getElementById('kunde-sammel-anzahl');
  const alleHaken = document.getElementById('kunde-zuordnung-alle');
  const sammelKnopf = document.getElementById('kunde-sammel-zuordnen');

  const gewaehlte = () => [...list.querySelectorAll('[data-auswahl="kunde"]:checked')];

  function zeigeAuswahl() {
    if (!leiste) return;
    const anzahl = gewaehlte().length;
    anzahlAnzeige.textContent = String(anzahl);
    leiste.classList.toggle('hidden', anzahl === 0);
    // The header box only ever reflects this page, which is why it is reset
    // rather than remembered when the fragment is swapped.
    if (alleHaken) {
      const alle = [...list.querySelectorAll('[data-auswahl="kunde"]')];
      alleHaken.checked = alle.length > 0 && anzahl === alle.length;
      alleHaken.indeterminate = anzahl > 0 && anzahl < alle.length;
    }
  }

  list.addEventListener('change', (event) => {
    if (event.target.matches('[data-auswahl="kunde"]')) zeigeAuswahl();
  });

  if (alleHaken) {
    alleHaken.addEventListener('change', () => {
      list.querySelectorAll('[data-auswahl="kunde"]').forEach((box) => {
        box.checked = alleHaken.checked;
      });
      zeigeAuswahl();
    });
  }

  if (sammelKnopf) {
    sammelKnopf.addEventListener('click', async () => {
      const ids = gewaehlte().map((box) => box.value);
      if (!ids.length) return;

      // Both may be present for the leadership, and either on its own is a
      // valid batch: a branch alone resolves to a person server-side, a
      // person alone places the rows that only lack an owner.
      const filiale = document.getElementById('kunde-sammel-filiale');
      const benutzer = document.getElementById('kunde-sammel-benutzer');
      const nutzlast = { ids };
      if (filiale && filiale.value) nutzlast.filiale = filiale.value;
      if (benutzer && benutzer.value) nutzlast.verantwortlichBenutzer = benutzer.value;

      if (!nutzlast.filiale && !nutzlast.verantwortlichBenutzer) {
        notifyError('Bitte zuerst eine Auswahl treffen.');
        (benutzer || filiale).focus();
        return;
      }

      sammelKnopf.disabled = true;
      showLoading(`${ids.length} Kunden werden zugeordnet …`);

      try {
        const antwort = await http.patch('/kunden/sammelzuordnung', nutzlast);
        await live.refresh();
        hideLoading();
        sammelKnopf.disabled = false;

        // The skipped ones are named rather than counted away: a customer
        // that stayed behind is the reader's next piece of work.
        const { zugeordnet, uebersprungen } = antwort.data.data;
        if (uebersprungen.length) {
          notifyError(`${zugeordnet} zugeordnet, ${uebersprungen.length} übersprungen.`);
        } else {
          notifySuccess(`${zugeordnet} Kunden wurden zugeordnet.`);
        }
        zeigeAuswahl();
      } catch (err) {
        hideLoading();
        sammelKnopf.disabled = false;
        notifyError(fehlermeldung(err));
      }
    });
  }
}
